import type { PDFDocumentProxy, PDFPageProxy } from 'pdfjs-dist';
import {
  getDocument,
  GlobalWorkerOptions,
  version as pdfjsVersion,
} from 'pdfjs-dist';
import { degrees, PDFDocument } from 'pdf-lib';

type RGB = { r: number; g: number; b: number };

export type CreateSheetPageDiffPdfOptions = {
  oldPdfUrl: string;
  newPdfUrl: string;
  pageNumber: number;
  maxRenderDimensionPx?: number;
  luminanceTolerance?: number;
  darknessDeltaThreshold?: number;
};

export type CreateSheetPageDiffPdfResult = {
  pdfBlob: Blob;
  totalPages: number;
};

const DEFAULT_MAX_RENDER_DIMENSION = 2200;
const DEFAULT_LUMINANCE_TOLERANCE = 6;
const DEFAULT_DARKNESS_DELTA_THRESHOLD = 8;
const ADDED_COLOR: RGB = { r: 34, g: 197, b: 94 };
const REMOVED_COLOR: RGB = { r: 220, g: 38, b: 38 };

function ensurePdfWorkerConfigured(): void {
  GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsVersion}/pdf.worker.min.js`;
}

async function loadPdf(url: string): Promise<PDFDocumentProxy> {
  try {
    return await getDocument({ url }).promise;
  } catch {
    return await getDocument({ url, disableRange: true }).promise;
  }
}

async function destroyPdf(pdf: PDFDocumentProxy): Promise<void> {
  try {
    pdf.cleanup();
    await pdf.destroy();
  } catch {
    // ignore cleanup failures
  }
}

function clampPageNumber(pageNumber: number, numPages: number): number {
  if (numPages <= 0) return 1;
  if (pageNumber < 1) return 1;
  if (pageNumber > numPages) return numPages;
  return pageNumber;
}

function calculateRenderScale(
  page: PDFPageProxy,
  maxRenderDimensionPx: number
): number {
  const baseViewport = page.getViewport({ scale: 1, rotation: 0 });
  const longestSide = Math.max(baseViewport.width, baseViewport.height);
  if (longestSide <= 0) return 1;
  const idealScale = maxRenderDimensionPx / longestSide;
  return Math.max(0.75, Math.min(2, idealScale));
}

async function renderPageToCanvas(
  page: PDFPageProxy,
  scale: number
): Promise<HTMLCanvasElement> {
  const viewport = page.getViewport({ scale, rotation: 0 });
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.ceil(viewport.width));
  canvas.height = Math.max(1, Math.ceil(viewport.height));
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Canvas 2D context not available');
  }

  const renderTask = page.render({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    canvasContext: ctx as any,
    viewport,
    background: 'rgba(255,255,255,1)',
  });
  await renderTask.promise;

  return canvas;
}

function resizeCanvas(
  source: HTMLCanvasElement,
  width: number,
  height: number
): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Canvas 2D context not available');
  }
  ctx.drawImage(source, 0, 0, width, height);
  return canvas;
}

function getLuminance(r: number, g: number, b: number): number {
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function blend(base: number, overlay: number, alpha: number): number {
  return Math.round(base * (1 - alpha) + overlay * alpha);
}

type PixelChannels = {
  r: number;
  g: number;
  b: number;
  a: number;
};

function resolveDiffPixel({
  oldLuminance,
  newLuminance,
  oldPixel,
  newPixel,
  luminanceTolerance,
  darknessDeltaThreshold,
}: {
  oldLuminance: number;
  newLuminance: number;
  oldPixel: PixelChannels;
  newPixel: PixelChannels;
  luminanceTolerance: number;
  darknessDeltaThreshold: number;
}): PixelChannels {
  const luminanceDelta = Math.abs(newLuminance - oldLuminance);
  if (luminanceDelta <= luminanceTolerance) {
    return newPixel;
  }

  const additionStrength =
    oldLuminance - newLuminance > darknessDeltaThreshold
      ? Math.min(0.95, 0.4 + (oldLuminance - newLuminance) / 220)
      : 0;
  const removalStrength =
    newLuminance - oldLuminance > darknessDeltaThreshold
      ? Math.min(0.95, 0.4 + (newLuminance - oldLuminance) / 220)
      : 0;

  if (additionStrength > 0 && additionStrength >= removalStrength) {
    return {
      r: blend(newPixel.r, ADDED_COLOR.r, additionStrength),
      g: blend(newPixel.g, ADDED_COLOR.g, additionStrength),
      b: blend(newPixel.b, ADDED_COLOR.b, additionStrength),
      a: Math.max(newPixel.a, oldPixel.a),
    };
  }

  if (removalStrength > 0) {
    return {
      r: blend(newPixel.r, REMOVED_COLOR.r, removalStrength),
      g: blend(newPixel.g, REMOVED_COLOR.g, removalStrength),
      b: blend(newPixel.b, REMOVED_COLOR.b, removalStrength),
      a: Math.max(newPixel.a, oldPixel.a),
    };
  }

  return newPixel;
}

function buildDiffCanvas({
  oldCanvas,
  newCanvas,
  luminanceTolerance,
  darknessDeltaThreshold,
}: {
  oldCanvas: HTMLCanvasElement;
  newCanvas: HTMLCanvasElement;
  luminanceTolerance: number;
  darknessDeltaThreshold: number;
}): HTMLCanvasElement {
  const width = newCanvas.width;
  const height = newCanvas.height;
  const output = document.createElement('canvas');
  output.width = width;
  output.height = height;

  const oldCtx = oldCanvas.getContext('2d');
  const newCtx = newCanvas.getContext('2d');
  const outCtx = output.getContext('2d');
  if (!oldCtx || !newCtx || !outCtx) {
    throw new Error('Canvas 2D context not available');
  }

  const oldImage = oldCtx.getImageData(0, 0, width, height);
  const newImage = newCtx.getImageData(0, 0, width, height);
  const result = new ImageData(width, height);
  const oldData = oldImage.data;
  const newData = newImage.data;
  const outData = result.data;

  for (let index = 0; index < newData.length; index += 4) {
    const oldPixel: PixelChannels = {
      r: oldData[index],
      g: oldData[index + 1],
      b: oldData[index + 2],
      a: oldData[index + 3],
    };
    const newPixel: PixelChannels = {
      r: newData[index],
      g: newData[index + 1],
      b: newData[index + 2],
      a: newData[index + 3],
    };
    const oldAlpha = oldPixel.a / 255;
    const newAlpha = newPixel.a / 255;

    const oldLuminance =
      getLuminance(oldPixel.r, oldPixel.g, oldPixel.b) * oldAlpha +
      255 * (1 - oldAlpha);
    const newLuminance =
      getLuminance(newPixel.r, newPixel.g, newPixel.b) * newAlpha +
      255 * (1 - newAlpha);

    const resolvedPixel = resolveDiffPixel({
      oldLuminance,
      newLuminance,
      oldPixel,
      newPixel,
      luminanceTolerance,
      darknessDeltaThreshold,
    });

    outData[index] = resolvedPixel.r;
    outData[index + 1] = resolvedPixel.g;
    outData[index + 2] = resolvedPixel.b;
    outData[index + 3] = resolvedPixel.a;
  }

  outCtx.putImageData(result, 0, 0);
  return output;
}

async function canvasToPngBytes(canvas: HTMLCanvasElement): Promise<Uint8Array> {
  const maybeBlob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/png');
  });

  if (!maybeBlob) {
    throw new Error('Unable to create PNG from diff canvas');
  }
  return new Uint8Array(await maybeBlob.arrayBuffer());
}

function normalizeRotation(rotation: number): 0 | 90 | 180 | 270 {
  const normalized = ((rotation % 360) + 360) % 360;
  if (normalized === 90 || normalized === 180 || normalized === 270) {
    return normalized;
  }
  return 0;
}

// Create a synthetic one-page PDF where unchanged pixels remain unchanged,
// additions are highlighted green, and removals are highlighted red.
export async function createSheetPageDiffPdf(
  options: CreateSheetPageDiffPdfOptions
): Promise<CreateSheetPageDiffPdfResult> {
  ensurePdfWorkerConfigured();

  const {
    oldPdfUrl,
    newPdfUrl,
    pageNumber,
    maxRenderDimensionPx = DEFAULT_MAX_RENDER_DIMENSION,
    luminanceTolerance = DEFAULT_LUMINANCE_TOLERANCE,
    darknessDeltaThreshold = DEFAULT_DARKNESS_DELTA_THRESHOLD,
  } = options;

  const [oldPdf, newPdf] = await Promise.all([loadPdf(oldPdfUrl), loadPdf(newPdfUrl)]);

  try {
    const targetNewPage = clampPageNumber(pageNumber, newPdf.numPages);
    const targetOldPage = clampPageNumber(pageNumber, oldPdf.numPages);
    const [oldPage, newPage] = await Promise.all([
      oldPdf.getPage(targetOldPage),
      newPdf.getPage(targetNewPage),
    ]);

    const renderScale = calculateRenderScale(newPage, maxRenderDimensionPx);
    const [oldRendered, newRendered] = await Promise.all([
      renderPageToCanvas(oldPage, renderScale),
      renderPageToCanvas(newPage, renderScale),
    ]);
    const oldCanvas =
      oldRendered.width === newRendered.width &&
      oldRendered.height === newRendered.height
        ? oldRendered
        : resizeCanvas(oldRendered, newRendered.width, newRendered.height);

    const diffCanvas = buildDiffCanvas({
      oldCanvas,
      newCanvas: newRendered,
      luminanceTolerance,
      darknessDeltaThreshold,
    });

    const pngBytes = await canvasToPngBytes(diffCanvas);
    const pageView = newPage.view as [number, number, number, number];
    const pageWidth = Math.max(1, Math.abs(pageView[2] - pageView[0]));
    const pageHeight = Math.max(1, Math.abs(pageView[3] - pageView[1]));
    const rotation = normalizeRotation(newPage.rotate || 0);

    const pdfDoc = await PDFDocument.create();
    const page = pdfDoc.addPage([pageWidth, pageHeight]);
    page.setRotation(degrees(rotation));

    const embedded = await pdfDoc.embedPng(pngBytes);
    page.drawImage(embedded, {
      x: 0,
      y: 0,
      width: pageWidth,
      height: pageHeight,
    });

    const pdfBytes = await pdfDoc.save();
    return {
      pdfBlob: new Blob([pdfBytes], { type: 'application/pdf' }),
      totalPages: newPdf.numPages,
    };
  } finally {
    await Promise.all([destroyPdf(oldPdf), destroyPdf(newPdf)]);
  }
}
